import { Card } from '@/common/ui/Card/Card'
import { cn } from '@/common/lib/cn/cn'
import type { BrandComparisonBrandData } from '../../model/useCompanyStatsBrandComparison'
import { computeDelta, type DeltaResult } from '../../lib/brandComparison'
import { formatScoreValue } from '../../lib/scoreColors'
import { BrandComparisonDeltaBadge } from './BrandComparisonDeltaBadge'
import { CompanyStatsBrandComparisonLegendDot } from './CompanyStatsBrandComparisonHeader'

type CompanyStatsBrandComparisonSummaryCardsProps = {
    brandA: BrandComparisonBrandData
    brandB: BrandComparisonBrandData
}

type ComparisonSummaryCardProps = {
    label: string
    brandAName: string
    brandBName: string
    brandAValue: string
    brandBValue: string
    delta: DeltaResult
    deltaFractionDigits?: number
    brandAValueClassName?: string
    brandBValueClassName?: string
}

const ComparisonSummaryCard = ({
    label,
    brandAName,
    brandBName,
    brandAValue,
    brandBValue,
    delta,
    deltaFractionDigits = 1,
    brandAValueClassName = 'text-[#08AC81]',
    brandBValueClassName = 'text-[#8b5cf6]',
}: ComparisonSummaryCardProps) => (
    <Card color='primary' className='flex min-w-[140px] flex-1 flex-col gap-4'>
        <p className='text-xs font-bold uppercase tracking-wide text-white'>{label}</p>

        <div className='grid grid-cols-[1fr_auto_1fr] items-stretch gap-3'>
            <div className='flex flex-col items-center gap-2 text-center'>
                <span className={cn('text-3xl font-semibold leading-none tabular-nums', brandAValueClassName)}>
                    {brandAValue}
                </span>
                <span className='inline-flex items-center gap-2 text-sm text-base-content/70'>
                    <CompanyStatsBrandComparisonLegendDot side='brandA' />
                    {brandAName}
                </span>
            </div>

            <div className='w-px self-stretch bg-white/10' aria-hidden />

            <div className='flex flex-col items-center gap-2 text-center'>
                <span className={cn('text-3xl font-semibold leading-none tabular-nums', brandBValueClassName)}>
                    {brandBValue}
                </span>
                <span className='inline-flex items-center gap-2 text-sm text-base-content/70'>
                    <CompanyStatsBrandComparisonLegendDot side='brandB' />
                    {brandBName}
                </span>
            </div>
        </div>
        <div className='flex items-center justify-between gap-1'>
            <BrandComparisonDeltaBadge delta={delta} variant='summary' fractionDigits={deltaFractionDigits} />
        </div>
    </Card>
)

export const CompanyStatsBrandComparisonSummaryCards = ({
    brandA,
    brandB,
}: CompanyStatsBrandComparisonSummaryCardsProps) => {
    const commonA = brandA.common
    const commonB = brandB.common

    return (
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5'>
            <ComparisonSummaryCard
                label='Средний балл'
                brandAName={brandA.name}
                brandBName={brandB.name}
                brandAValue={formatScoreValue(commonA.avg_score)}
                brandBValue={formatScoreValue(commonB.avg_score)}
                delta={computeDelta(commonA.avg_score, commonB.avg_score)}
            />
            <ComparisonSummaryCard
                label='Товаров'
                brandAName={brandA.name}
                brandBName={brandB.name}
                brandAValue={String(commonA.total_products)}
                brandBValue={String(commonB.total_products)}
                delta={computeDelta(commonA.total_products, commonB.total_products)}
                deltaFractionDigits={0}
                brandAValueClassName='text-white'
                brandBValueClassName='text-white'
            />
            <ComparisonSummaryCard
                label='Хорошие (≥8)'
                brandAName={brandA.name}
                brandBName={brandB.name}
                brandAValue={String(commonA.best?.count ?? 0)}
                brandBValue={String(commonB.best?.count ?? 0)}
                delta={computeDelta(commonA.best?.count ?? 0, commonB.best?.count ?? 0)}
                deltaFractionDigits={0}
            />
            <ComparisonSummaryCard
                label='Средние (6–7)'
                brandAName={brandA.name}
                brandBName={brandB.name}
                brandAValue={String(commonA.middle?.count ?? 0)}
                brandBValue={String(commonB.middle?.count ?? 0)}
                delta={computeDelta(commonA.middle?.count ?? 0, commonB.middle?.count ?? 0)}
                deltaFractionDigits={0}
                brandAValueClassName='text-[#F59E0B]'
                brandBValueClassName='text-[#F59E0B]'
            />
            <ComparisonSummaryCard
                label='Слабые (&lt;6)'
                brandAName={brandA.name}
                brandBName={brandB.name}
                brandAValue={String(commonA.low?.count ?? 0)}
                brandBValue={String(commonB.low?.count ?? 0)}
                delta={computeDelta(commonA.low?.count ?? 0, commonB.low?.count ?? 0)}
                deltaFractionDigits={0}
                brandAValueClassName='text-[#FF484B]'
                brandBValueClassName='text-[#FF484B]'
            />
        </div>
    )
}
